/**
 * Os cadastros em andamento da sessão (E189.F2, ADR-0138 §4), para o topo das listas da equipe e do
 * portal. Relê enquanto algum está `cadastrando`; o parado espera a pessoa e não pede releitura.
 * Quando um caso sai da lista, o sistema concluiu: `onConcluidos` avisa a página, que relê a lista
 * dela (os casos de cada CCB aparecem). A falha de rede mantém a última leitura e tenta de novo.
 */
import { useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { lerCadastros, type CadastroEmAndamentoData } from '@/lib/cadastro_sem_espera';

export const INTERVALO_DOS_CADASTROS_MS = 5000;

export function useCadastrosEmAndamento(
  client: AxiosInstance,
  onConcluidos: () => void,
): readonly CadastroEmAndamentoData[] {
  const [cadastros, setCadastros] = useState<readonly CadastroEmAndamentoData[]>([]);
  const avisar = useRef(onConcluidos);
  avisar.current = onConcluidos;

  useEffect(() => {
    let ativo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;
    let vistos = new Set<string>();
    const ciclo = async (): Promise<void> => {
      let lidos: CadastroEmAndamentoData[] | null = null;
      try {
        lidos = await lerCadastros(client);
      } catch {
        lidos = null;
      }
      if (!ativo) return;
      if (lidos !== null) {
        const agora = new Set(lidos.map((c) => c.case_id));
        if ([...vistos].some((id) => !agora.has(id))) avisar.current();
        vistos = agora;
        setCadastros(lidos);
        if (!lidos.some((c) => c.andamento.situacao === 'cadastrando')) return;
      }
      espera = setTimeout(() => void ciclo(), INTERVALO_DOS_CADASTROS_MS);
    };
    void ciclo();
    return () => {
      ativo = false;
      if (espera !== undefined) clearTimeout(espera);
    };
  }, [client]);

  return cadastros;
}
